/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import TableHead from '@mui/material/TableHead';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initParent = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  discount_type: 'B',
  discount_value: '',
  shipping: '',
  discount_unit: 'Y',
  vat_id: '2',
  status: 'edit',
  print_url: '',
  created_at: '',
  updated_at: '',
};

function ReceivePo() {
  const mod = 'receive';
  const app = useSelector((state) => state[APP_STATE]);
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [record, setRecord] = useState([]);
  const [parent, setParent] = useState(initParent);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const view = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/po`, app.token, {
      id: id,
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
      setLoading(false);
    });
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={20}>
                      No.
                    </TableCell>
                    <TableCell align='center' width={40}>
                      รูป
                    </TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='center' className='nowrap'>
                      ใบขอซื้อ
                    </TableCell>
                    <TableCell align='right' width={90} className='nowrap'>
                      จำนวน
                    </TableCell>
                    <TableCell>หน่วย</TableCell>
                    <TableCell align='right' width={90} className='nowrap'>
                      ราคาซื้อ
                    </TableCell>
                    <TableCell align='right' width={90} className='nowrap'>
                      แถม
                    </TableCell>
                    <TableCell align='right' width={90} className='nowrap'>
                      {parent.discount_unit === 'Y' ? 'ลด/หน่วย' : 'ลดรวม'}
                    </TableCell>
                    <TableCell align='right' width={90} className='nowrap'>
                      รับแล้ว
                    </TableCell>
                    <TableCell align='right' width={90} className='nowrap'>
                      ค้างรับ
                    </TableCell>
                    <TableCell align='right' width={90}>
                      ส่วนลด
                    </TableCell>
                    <TableCell align='right' width={90}>
                      รวม
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} className={row.active === 'Y' ? 'bg-yellow' : ''}>
                      <TableCell align='center'>{row.no}</TableCell>
                      <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.barcode2}</MDBox>
                      </TableCell>
                      <TableCell className='nowrap'>
                        <MDBox>{row.product_code}</MDBox>
                        <MDBox>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{row.pr_code}</TableCell>
                      <TableCell align='right' className='color-primary'>
                        {Func.numberDisplay(row.quantity)}
                      </TableCell>
                      <TableCell align='left'>{row.unit_name}</TableCell>
                      <TableCell align='right'>{row.price}</TableCell>
                      <TableCell align='right' className='color-primary'>
                        {row.quantity_free > 0 ? Func.numberDisplay(row.quantity_free) : '-'}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.discount_value > 0 ? (
                          <span>
                            {Func.numberDisplay(row.discount_value)}{' '}
                            {row.discount_type === 'P' ? '%' : '฿'}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align='right' className='color-success'>
                        {row.quantity_receive > 0 ? Func.numberDisplay(row.quantity_receive) : '-'}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.quantity_balance)}
                      </TableCell>
                      <TableCell align='right'>
                        {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
                      </TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>
              <Table aria-label='summary table'>
                <TableBody>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
                    </TableCell>
                    <TableCell align='right' width='200'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.total, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>หักส่วนลด</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.discount_value, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>
                        {parent.discount_type === 'P' ? '%' : 'บาท'}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.amount, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        ภาษีมูลค่าเพิ่ม {Func.numberDisplay(parent.vat_value, 0)}%
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>{Func.numberDisplay(parent.vat, 2)}</MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
                    </TableCell>
                    <TableCell align='right' width='200'>
                      <MDTypography variant='h5' color='primary'>
                        {Func.numberDisplay(parent.amount_vat, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
                    </TableCell>
                    <TableCell align='right' width='200'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.shipping, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
                    </TableCell>
                    <TableCell align='right' width='200'>
                      <MDTypography variant='h5' color='info'>
                        {Func.numberDisplay(parent.nettotal, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          #{parent.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      <MDButton
        tabIndex={-1}
        color='dark'
        size='small'
        onClick={() => {
          setModalOpen(true);
          view();
        }}
      >
        <Icon>touch_app</Icon> แสดง
      </MDButton>

      {renderOpen()}
      {notify}
    </div>
  );
}

export default ReceivePo;
